/**
 * Copyright (C) 2022 Panther Labs, Inc.
 *
 * The Panther SaaS is licensed under the terms of the Panther Enterprise Subscription
 * Agreement available at https://panther.com/enterprise-subscription-agreement/.
 * All intellectual property rights in and to the Panther SaaS, including any and all
 * rights to access the Panther SaaS, are governed by the Panther Enterprise Subscription Agreement.
 */

import React from 'react';
import { Box } from 'pounceV2';
import { useTheme } from '@mui/material';

interface ResizeHandleProps {
  onResize: (deltaX: number) => void;
}

/**
 * A draggable resize handle component that allows users to resize panels
 * Shows visual feedback on hover and during drag operations
 */
const ResizeHandleV3: React.FC<ResizeHandleProps> = ({ onResize }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const [isDragging, setIsDragging] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const startXRef = React.useRef<number>(0);

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;

    // Add global styles to prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  React.useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startXRef.current - e.clientX;
      startXRef.current = e.clientX;
      onResize(deltaX);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Remove global styles
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onResize]);

  // Cleanup cursor styles on unmount to prevent stuck cursor
  React.useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, []);

  return (
    <Box
      as="div"
      position="sticky"
      left={0}
      cursor="col-resize"
      zIndex={3}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        top: '50vh',
        width: '16px',
        height: 0,
        transform: 'translateY(-50%)',
      }}
      sx={
        {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-40px',
            left: 0,
            width: '16px',
            height: '80px',
            backgroundColor:
              isDragging || isHovering
                ? tokens.background.semantic.info.base
                : tokens.background.neutral.midToBrand[2],
            borderTopRightRadius: '8px',
            borderBottomRightRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease, width 0.2s ease',
          },
          '&:hover::before': {
            backgroundColor: tokens.background.semantic.info.base,
            width: '18px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '3px',
            height: '24px',
            backgroundColor: tokens.background.neutral.midToBrand.base,
            borderRadius: '2px',
            opacity: isDragging || isHovering ? 1 : 0.6,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
          },
        } as any
      }
    />
  );
};

export default ResizeHandleV3;
